import Link from 'next/link';

import {
  ArrowRight,
  BadgeDollarSign,
  Calculator,
  ClipboardList,
  Clock,
  FileText,
  Package,
  Percent,
  Ship,
  ShoppingCart,
  TrendingDown,
} from 'lucide-react';

import { Card, CardContent } from '@kit/ui/card';

const ALL_TOOLS = [
  {
    title: 'Purchase Order Creator',
    href: '/free-purchase-order-creator',
    icon: ClipboardList,
    description: 'Build a purchase order online and download it as a PDF',
    iconClass: 'bg-primary/10 text-primary',
  },
  {
    title: 'Amazon Profit Calculator',
    href: '/tools/amazon-profit-calculator',
    icon: BadgeDollarSign,
    description: 'Profit after landed cost, Amazon fees, ads, and returns',
    iconClass: 'bg-primary/10 text-primary',
  },
  {
    title: 'Dropshipping Profit Calculator',
    href: '/tools/dropshipping-profit-calculator',
    icon: ShoppingCart,
    description: 'Per-order profit after product, shipping, ads, and fees',
    iconClass: 'bg-primary/10 text-primary',
  },
  {
    title: 'Profit Margin Calculator',
    href: '/tools/profit-margin-calculator',
    icon: Percent,
    description: 'Gross vs net, markup, and the price for a target margin',
    iconClass: 'bg-accent-tint text-accent-deep',
  },
  {
    title: 'Import Duty Calculator',
    href: '/tools/import-duty-calculator',
    icon: Calculator,
    description: 'Live HTS rate plus CBP fees, before you place the PO',
    iconClass: 'bg-primary/10 text-primary',
  },
  {
    title: 'Landed Cost Calculator',
    href: '/tools/landed-cost-calculator',
    icon: TrendingDown,
    description: 'Cost per unit after product, freight, and duty',
    iconClass: 'bg-accent-tint text-accent-deep',
  },
  {
    title: 'Freight Cost Estimator',
    href: '/tools/freight-cost-estimator',
    icon: Ship,
    description:
      'Compare sea and air freight cost, chargeable weight, and transit time',
    iconClass: 'bg-sage-bg text-sage-fg',
  },
  {
    title: 'MOQ Calculator',
    href: '/tools/moq-calculator',
    icon: Package,
    description: 'EOQ vs factory MOQ, and the annual premium of the minimum',
    iconClass: 'bg-accent-tint text-accent-deep',
  },
  {
    title: 'Lead Time Calculator',
    href: '/tools/lead-time-calculator',
    icon: Clock,
    description:
      'Lead time, reorder point, and the latest date the next PO can go out',
    iconClass: 'bg-warn-bg text-warn-fg',
  },
  {
    title: 'Free Templates',
    href: '/tools/templates',
    icon: FileText,
    description:
      'Excel templates for POs, packing lists, invoices, inventory, and P&L',
    iconClass: 'bg-destructive/10 text-destructive',
  },
];

export function RelatedTools({ currentPath }: { currentPath: string }) {
  const tools = ALL_TOOLS.filter((t) => t.href !== currentPath);

  return (
    <div className="mx-auto mt-16 max-w-5xl">
      <div className="mb-6 flex items-center justify-between">
        <h2 className="text-foreground text-2xl font-bold">More Free Tools</h2>
        <Link
          href="/tools"
          className="text-primary flex items-center gap-1 text-sm font-medium hover:underline"
        >
          View all
          <ArrowRight className="h-3.5 w-3.5" />
        </Link>
      </div>
      <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-3">
        {tools.slice(0, 6).map((tool) => {
          const Icon = tool.icon;
          return (
            <Link
              key={tool.href}
              href={tool.href}
              className="group block transition-all hover:-translate-y-0.5"
            >
              <Card className="border-border h-full shadow-sm transition-shadow group-hover:shadow-md">
                <CardContent className="flex items-start gap-4 p-5">
                  <div
                    className={`mt-0.5 flex h-10 w-10 shrink-0 items-center justify-center rounded-lg ${tool.iconClass}`}
                  >
                    <Icon className="h-5 w-5" />
                  </div>
                  <div className="min-w-0">
                    <h3 className="text-foreground mb-1 text-sm leading-tight font-semibold">
                      {tool.title}
                    </h3>
                    <p className="text-muted-foreground line-clamp-2 text-xs leading-relaxed">
                      {tool.description}
                    </p>
                  </div>
                </CardContent>
              </Card>
            </Link>
          );
        })}
      </div>
    </div>
  );
}
